import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import employeeService from '../services/employeeService';
import authService from '../services/authService';
import MasterPageWrapper from './masters/common/MasterPageWrapper';
import FormSection from './masters/common/FormSection';
import CommonPagination from '../components/common/CommonPagination';
import PAGINATION_CONFIG from '../config/paginationConfig';
import DiscountAuthoritySection from './masters/components/DiscountAuthoritySection';
import discountService from '../services/discountService';
import biometricAuthService from '../services/biometric/biometricAuthService';
import faceAuthService from '../services/biometric/faceAuthService';
import FaceBiometricModal from '../components/biometric/FaceBiometricModal';
import ReAuthenticateModal from '../components/auth/ReAuthenticateModal';
import { refundFieldsForSave } from '../utils/refundAuthority';
import './EmployeePage.css';

const INITIAL_FORM_STATE = {
  title: 'Mr.',
  firstName: '',
  lastName: '',
  name: '',
  mobile: '',
  email: '',
  dob: '',
  panNo: '',
  passportNo: '',
  qualification: '',
  medicalRegNo: '',
  regAuthority: '',
  designation: '',
  accessDepartment: '',
  startDate: new Date().toISOString().split('T')[0],
  username: '',
  password: '',
  passwordChangeFrequencyInDays: 0,
  isPasswordChangeRequired: false,
  passwordLastChangedDate: null,
  maxFailedLoginAttempts: 3,
  isLoginBlocked: false,
  centreId: 1,
  roleId: 2,
};

const INITIAL_ACCESS_CONFIG = {
  allowedConcurrentLogins: 1,
  loginRestrictionEnabled: false,
  loginStartTime: '09:00:00',
  loginEndTime: '19:00:00',
  allowedDays: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
  timezone: 'Asia/Kolkata',
  sessionTimeoutMinutes: 480,
  idleTimeoutMinutes: 60,
};

const DAYS_OF_WEEK = [
  { code: 'MON', label: 'Mon' },
  { code: 'TUE', label: 'Tue' },
  { code: 'WED', label: 'Wed' },
  { code: 'THU', label: 'Thu' },
  { code: 'FRI', label: 'Fri' },
  { code: 'SAT', label: 'Sat' },
  { code: 'SUN', label: 'Sun' },
];

/**
 * Reusable searchable multi-select dropdown with chip display,
 * easy remove options, select/clear all, and outside-click dismiss.
 */
const MultiSelectDropdown = ({
  label,
  options = [],
  selectedIds = [],
  onChange,
  placeholder = '+ Select options...',
  searchPlaceholder = 'Search...',
  required = false,
  error = null,
  chipClass = 'emp-chip-default',
  id = '',
  actionButtonText = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef(null);

  // Dismiss on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (opt) =>
        (opt.name && opt.name.toLowerCase().includes(q)) ||
        (opt.code && opt.code.toLowerCase().includes(q))
    );
  }, [options, query]);

  const handleToggle = (optId) => {
    if (selectedIds.includes(optId)) {
      onChange(selectedIds.filter((item) => item !== optId));
    } else {
      onChange([...selectedIds, optId]);
    }
  };

  const handleRemove = (e, optId) => {
    e.stopPropagation();
    onChange(selectedIds.filter((item) => item !== optId));
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    onChange(options.map((opt) => opt.id));
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedOptions = options.filter((opt) => selectedIds.includes(opt.id));

  return (
    <div className="emp-form-group emp-multiselect-group" ref={dropdownRef} id={id}>
      <div className="emp-multiselect-label-row">
        <label className="emp-form-label">
          {label} {required && <span className="emp-form-required">*</span>}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="emp-multiselect-counter">
            {selectedIds.length} of {options.length} Selected
          </span>
          {actionButtonText && (
            <button
              type="button"
              className="emp-multiselect-header-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(true);
              }}
            >
              {actionButtonText}
            </button>
          )}
        </div>
      </div>

      <div
        className={`emp-multiselect-box ${isOpen ? 'emp-multiselect-open' : ''} ${
          error ? 'emp-form-control-error' : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
      >
        <div className="emp-multiselect-chips-wrap">
          {selectedOptions.length === 0 ? (
            <span className="emp-multiselect-placeholder">{placeholder}</span>
          ) : (
            selectedOptions.map((opt) => (
              <span key={opt.id} className={`emp-multiselect-chip ${chipClass}`}>
                <span className="emp-chip-text">
                  {opt.name} {opt.code && opt.code !== opt.name ? `(${opt.code})` : ''}
                </span>
                <button
                  type="button"
                  className="emp-chip-remove-btn"
                  onClick={(e) => handleRemove(e, opt.id)}
                  title={`Remove ${opt.name}`}
                >
                  &times;
                </button>
              </span>
            ))
          )}
        </div>

        <div className="emp-multiselect-box-actions">
          <span className="emp-multiselect-caret">{isOpen ? '▲' : '▼'}</span>
        </div>
      </div>

      {error && <span className="emp-form-err-msg">{error}</span>}

      {/* Dropdown Popover */}
      {isOpen && (
        <div className="emp-multiselect-dropdown-menu">
          <div className="emp-multiselect-search-header">
            <input
              type="text"
              className="emp-multiselect-search-input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            <div className="emp-multiselect-quick-actions">
              <button
                type="button"
                className="emp-multiselect-action-btn"
                onClick={handleSelectAll}
              >
                Select All
              </button>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <button
                type="button"
                className="emp-multiselect-action-btn"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="emp-multiselect-options-list">
            {filteredOptions.length === 0 ? (
              <div className="emp-multiselect-no-results">No matches found.</div>
            ) : (
              filteredOptions.map((opt) => {
                const isChecked = selectedIds.includes(opt.id);
                return (
                  <div
                    key={opt.id}
                    className={`emp-multiselect-option-item ${
                      isChecked ? 'emp-option-selected' : ''
                    }`}
                    onClick={() => handleToggle(opt.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}} // handled by click
                      className="emp-multiselect-checkbox"
                    />
                    <span className="emp-option-name">{opt.name}</span>
                    {opt.code && opt.code !== opt.name && (
                      <span className="emp-option-code">({opt.code})</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const EmployeePage = () => {
  // Master list state
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);
  const [isLoading, setIsLoading] = useState(false);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Dropdown Lookups
  const [roles, setRoles] = useState([]);
  const [centres, setCentres] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // Access & Assignment (Multiple Centres & Multiple Roles)
  const [selectedCentreIds, setSelectedCentreIds] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  // Toast / Notification
  const [toast, setToast] = useState(null);

  // Discount Authority Staged Draft (for new & edited employee)
  const [discountAuthorityDraft, setDiscountAuthorityDraft] = useState(null);

  // Screen navigation & Form state (Form opens first by default)
  const [showForm, setShowForm] = useState(true);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [currentEmpId, setCurrentEmpId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isReAuthModalOpen, setIsReAuthModalOpen] = useState(false);

  // Admin Quick Password Reset (no admin self-re-auth needed; for when the employee's/your own current password is forgotten)
  const [quickResetModal, setQuickResetModal] = useState({ open: false, newPassword: '', forceChange: true, isSaving: false });
  const [showSessionPolicyDetails, setShowSessionPolicyDetails] = useState(true);

  // Username validation state
  const [usernameAvailability, setUsernameAvailability] = useState(null); // null | 'checking' | 'available' | 'taken'
  const usernameDebounceRef = useRef(null);

  // View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [viewWorkspaces, setViewWorkspaces] = useState([]);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewEmployeePasskeys, setViewEmployeePasskeys] = useState([]);

  // Status toggle confirmation modal
  const [statusModal, setStatusModal] = useState({ open: false, employee: null });
  const [isStatusToggling, setIsStatusToggling] = useState(false);

  // Authentication & Security (Biometric Passkeys) state
  const [passkeyCredentials, setPasskeyCredentials] = useState([]);
  const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(false);
  const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [newDeviceFriendlyName, setNewDeviceFriendlyName] = useState('Office Laptop');
  const [passkeyError, setPasskeyError] = useState(null);

  // Facial Biometric State
  const [faceBiometricStatus, setFaceBiometricStatus] = useState({ isEnrolled: false });
  const [showFaceModal, setShowFaceModal] = useState(false);
  const [viewEmployeeFace, setViewEmployeeFace] = useState({ isEnrolled: false });

  // Access Control & Live Sessions state
  const [accessConfig, setAccessConfig] = useState(INITIAL_ACCESS_CONFIG);
  const [sessionsModal, setSessionsModal] = useState({ open: false, employee: null, sessions: [], loading: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Load Lookups once
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [rolesRes, centresRes, deptsRes, desigRes] = await Promise.all([
          employeeService.getRolesLookup(),
          employeeService.getCentresLookup(),
          employeeService.getDepartmentsLookup(),
          employeeService.getDesignationsLookup(),
        ]);
        if (rolesRes.success) setRoles(rolesRes.data || []);
        if (centresRes.success) setCentres(centresRes.data || []);
        if (deptsRes.success) setDepartments(deptsRes.data || []);
        if (desigRes.success) setDesignations(desigRes.data || []);
      } catch (err) {
        console.error('Failed to load lookup lists:', err);
      }
    };
    fetchLookups();
  }, []);

  // Fetch Employees
  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page,
        pageSize,
        search: searchTerm || undefined,
        roleId: selectedRoleId ? parseInt(selectedRoleId, 10) : undefined,
        centreId: selectedCentreId ? parseInt(selectedCentreId, 10) : undefined,
        isActive: selectedStatus !== '' ? selectedStatus === 'true' : undefined,
      };

      const res = await employeeService.getEmployees(params);
      if (res.success && res.data) {
        setEmployees(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
      } else {
        setEmployees([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      showToast(err.response?.data?.message || 'Failed to retrieve employee catalog.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchTerm, selectedRoleId, selectedCentreId, selectedStatus]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Username live uniqueness check
  const handleUsernameChange = (val) => {
    const username = val.trim();
    setFormData((prev) => ({ ...prev, username }));
    if (formErrors.username) {
      setFormErrors((prev) => ({ ...prev, username: null }));
    }

    if (!username) {
      setUsernameAvailability(null);
      return;
    }

    setUsernameAvailability('checking');
    if (usernameDebounceRef.current) {
      clearTimeout(usernameDebounceRef.current);
    }

    usernameDebounceRef.current = setTimeout(async () => {
      try {
        const res = await employeeService.checkUsername(username, formMode === 'edit' ? currentEmpId : null);
        if (res.data?.available) {
          setUsernameAvailability('available');
        } else {
          setUsernameAvailability('taken');
        }
      } catch (err) {
        setUsernameAvailability(null);
      }
    }, 450);
  };

  // Fetch registered passkeys for an employee
  const fetchPasskeyCredentials = async (empId) => {
    if (!empId) {
      setPasskeyCredentials([]);
      return;
    }
    setIsLoadingPasskeys(true);
    try {
      const creds = await biometricAuthService.getEmployeeCredentials(empId);
      setPasskeyCredentials(creds || []);
    } catch (err) {
      console.warn('Failed to load employee passkey credentials:', err);
      setPasskeyCredentials([]);
    } finally {
      setIsLoadingPasskeys(false);
    }
  };

  // Perform WebAuthn registration ceremony for current employee
  const handleRegisterPasskey = async () => {
    if (!currentEmpId) {
      showToast('Please save employee details first before registering biometric passkey.', 'error');
      return;
    }
    setIsRegisteringPasskey(true);
    setPasskeyError(null);
    try {
      await biometricAuthService.registerPasskey(currentEmpId, newDeviceFriendlyName || 'Workstation / Laptop');
      showToast('Biometric / Passkey credential successfully registered for staff member!');
      setShowPasskeyModal(false);
      await fetchPasskeyCredentials(currentEmpId);
    } catch (err) {
      console.error('Passkey registration error:', err);
      const serverMsg = err.response?.data?.message || (typeof err.response?.data === 'string' ? err.response?.data : null);
      const msg = serverMsg || err.message || 'Passkey enrollment was cancelled or failed.';
      setPasskeyError(msg);
      showToast(msg, 'error');
    } finally {
      setIsRegisteringPasskey(false);
    }
  };

  // Revoke a registered passkey credential
  const handleRevokePasskey = async (credentialId, deviceName) => {
    if (!window.confirm(`Revoke secure login access for "${deviceName || 'this device'}"? Staff will no longer be able to log in with this passkey.`)) {
      return;
    }
    try {
      await biometricAuthService.revokeCredential(credentialId, currentEmpId);
      showToast(`Credential for "${deviceName || 'device'}" revoked.`);
      await fetchPasskeyCredentials(currentEmpId);
    } catch (err) {
      showToast(err.message || 'Failed to revoke credential.', 'error');
    }
  };

  // Fetch facial biometric enrollment status for an employee
  const fetchFaceBiometricStatus = async (empId) => {
    if (!empId) {
      setFaceBiometricStatus({ isEnrolled: false });
      return;
    }
    try {
      const status = await faceAuthService.getEmployeeFaceStatus(empId);
      setFaceBiometricStatus(status || { isEnrolled: false });
    } catch (err) {
      console.warn('Failed to load employee face status:', err);
      setFaceBiometricStatus({ isEnrolled: false });
    }
  };

  // Perform Face Enrollment submission
  const handleEnrollFaceSuccess = async ({ faceEncoding, quality }) => {
    if (!currentEmpId) {
      showToast('Please save employee details first before enrolling facial biometrics.', 'error');
      return;
    }
    try {
      await faceAuthService.enrollEmployeeFace(currentEmpId, faceEncoding, quality);
      showToast('Face biometric template enrolled successfully for staff member!');
      setShowFaceModal(false);
      await fetchFaceBiometricStatus(currentEmpId);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Face enrollment failed.';
      showToast(msg, 'error');
      throw err;
    }
  };

  // Revoke Face Biometric template
  const handleRevokeFaceBiometric = async () => {
    if (!window.confirm(`Revoke enrolled facial recognition template for "${formData.name || 'this staff member'}"? They will no longer be able to log in with face recognition.`)) {
      return;
    }
    try {
      await faceAuthService.revokeEmployeeFace(currentEmpId);
      showToast('Facial recognition template revoked.');
      await fetchFaceBiometricStatus(currentEmpId);
    } catch (err) {
      showToast(err.message || 'Failed to revoke facial biometrics.', 'error');
    }
  };

  // Open Create Form
  const handleOpenCreate = () => {
    setFormData(INITIAL_FORM_STATE);
    setAccessConfig(INITIAL_ACCESS_CONFIG);
    setSelectedCentreIds([]);
    setSelectedRoleIds([]);
    setFormMode('create');
    setCurrentEmpId(null);
    setFormErrors({});
    setUsernameAvailability(null);
    setPasskeyCredentials([]);
    setFaceBiometricStatus({ isEnrolled: false });
    setShowForm(true);
  };

  // Open Edit Form
  const handleOpenEdit = async (empId) => {
    try {
      setIsLoading(true);
      const [res, wsRes] = await Promise.all([
        employeeService.getEmployeeById(empId),
        employeeService.getUserWorkspaces(empId).catch(() => ({ success: false })),
      ]);

      // Also load existing passkey credentials and access config for this employee
      fetchPasskeyCredentials(empId);
      fetchFaceBiometricStatus(empId);
      employeeService.getAccessConfig(empId)
        .then((cfgRes) => {
          if (cfgRes.success && cfgRes.data) {
            setAccessConfig({
              allowedConcurrentLogins: cfgRes.data.allowedConcurrentLogins || 1,
              loginRestrictionEnabled: !!cfgRes.data.loginRestrictionEnabled,
              loginStartTime: cfgRes.data.loginStartTime || '09:00:00',
              loginEndTime: cfgRes.data.loginEndTime || '19:00:00',
              allowedDays: (cfgRes.data.allowedDays || 'MON,TUE,WED,THU,FRI,SAT,SUN')
                .split(',')
                .map((d) => d.trim().toUpperCase()),
              timezone: cfgRes.data.timezone || 'Asia/Kolkata',
              sessionTimeoutMinutes: cfgRes.data.sessionTimeoutMinutes || 480,
              idleTimeoutMinutes: cfgRes.data.idleTimeoutMinutes || 60,
            });
          }
        })
        .catch(() => setAccessConfig(INITIAL_ACCESS_CONFIG));

      if (res.success && res.data) {
        const emp = res.data;
        setCurrentEmpId(empId);
        setFormMode('edit');

        const nameParts = (emp.name || '').trim().split(' ');
        const fName = nameParts[0] || '';
        const lName = nameParts.slice(1).join(' ') || '';

        setFormData({
          title: emp.title || 'Mr.',
          firstName: fName,
          lastName: lName,
          name: emp.name || '',
          mobile: emp.mobile || '',
          email: emp.email || '',
          dob: emp.dob ? emp.dob.split('T')[0] : '',
          panNo: emp.panNo || '',
          passportNo: emp.passportNo || '',
          qualification: emp.qualification || '',
          medicalRegNo: emp.medicalRegNo || '',
          regAuthority: emp.regAuthority || '',
          designation: emp.designation || '',
          accessDepartment: emp.accessDepartment || '',
          startDate: emp.startDate ? emp.startDate.split('T')[0] : '',
          username: emp.userName || '',
          password: '', // Blank implies unchanged on edit
          passwordChangeFrequencyInDays: emp.passwordChangeFrequencyInDays ?? 0,
          isPasswordChangeRequired: !!emp.isPasswordChangeRequired,
          passwordLastChangedDate: emp.passwordLastChangedDate || null,
          maxFailedLoginAttempts: emp.maxFailedLoginAttempts ?? 3,
          centreId: emp.centreId || 1,
          roleId: emp.roleId || 2,
          isLoginBlocked: !!emp.isLoginBlocked,
        });

        // Resolve assigned centres and roles
        let assignedCentres = [];
        let assignedRoles = [];
        if (wsRes.success && Array.isArray(wsRes.data) && wsRes.data.length > 0) {
          assignedCentres = [...new Set(wsRes.data.map((w) => w.centreId))];
          assignedRoles = [...new Set(wsRes.data.map((w) => w.roleId))];
        }

        if (assignedCentres.length === 0 && emp.centreId) {
          assignedCentres = [emp.centreId];
        }
        if (assignedRoles.length === 0 && emp.roleId) {
          assignedRoles = [emp.roleId];
        }

        setSelectedCentreIds(assignedCentres);
        setSelectedRoleIds(assignedRoles);
        setFormErrors({});
        setUsernameAvailability('available');
        setShowForm(true);
      }
    } catch (err) {
      showToast('Unable to load employee details for editing.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Active Workstation Sessions Management
  const handleOpenSessionsModal = async (emp) => {
    setSessionsModal({ open: true, employee: emp, sessions: [], loading: true });
    try {
      const res = await employeeService.getActiveSessions(emp.employeeId);
      if (res.success) {
        setSessionsModal({ open: true, employee: emp, sessions: res.data || [], loading: false });
      } else {
        setSessionsModal({ open: true, employee: emp, sessions: [], loading: false });
      }
    } catch (err) {
      showToast('Failed to load active sessions.', 'error');
      setSessionsModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleForceLogoutSession = async (sessionId) => {
    try {
      const res = await employeeService.forceLogoutSession(sessionId, 'Admin force logout from UI');
      if (res.success) {
        showToast('Workstation session forcefully terminated.');
        if (sessionsModal.employee) {
          const fresh = await employeeService.getActiveSessions(sessionsModal.employee.employeeId);
          if (fresh.success) {
            setSessionsModal((prev) => ({ ...prev, sessions: fresh.data || [] }));
          }
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to terminate session.', 'error');
    }
  };

  const handleLogoutAllSessions = async (empId) => {
    try {
      const res = await employeeService.logoutAllSessions(empId, 'Admin terminated all sessions from UI');
      if (res.success) {
        showToast('All active sessions terminated.');
        setSessionsModal((prev) => ({ ...prev, sessions: [] }));
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to terminate all sessions.', 'error');
    }
  };

  // Reset Form
  const handleResetForm = () => {
    if (formMode === 'edit' && currentEmpId) {
      handleOpenEdit(currentEmpId);
    } else {
      handleOpenCreate();
    }
  };

  // Open View Modal
  const handleOpenView = async (empId) => {
    setIsViewLoading(true);
    setIsViewModalOpen(true);
    try {
      const [res, wsRes, passkeysRes, faceRes] = await Promise.all([
        employeeService.getEmployeeById(empId),
        employeeService.getUserWorkspaces(empId).catch(() => ({ success: false })),
        biometricAuthService.getEmployeeCredentials(empId).catch(() => []),
        faceAuthService.getEmployeeFaceStatus(empId).catch(() => ({ isEnrolled: false })),
      ]);
      if (res.success && res.data) {
        setViewEmployee(res.data);
      }
      if (wsRes.success && Array.isArray(wsRes.data)) {
        setViewWorkspaces(wsRes.data);
      } else {
        setViewWorkspaces([]);
      }
      setViewEmployeePasskeys(passkeysRes || []);
      setViewEmployeeFace(faceRes || { isEnrolled: false });
    } catch (err) {
      showToast('Unable to load employee profile.', 'error');
      setIsViewModalOpen(false);
    } finally {
      setIsViewLoading(false);
    }
  };

  // Deduplicated authorized centres for View modal
  const viewDistinctCentres = useMemo(() => {
    if (!viewWorkspaces || viewWorkspaces.length === 0) {
      if (viewEmployee?.centreName) {
        return [{
          centreId: viewEmployee.centreId || 1,
          centreName: viewEmployee.centreName,
          roles: viewEmployee.roleName ? [viewEmployee.roleName] : [],
          isDefault: true,
        }];
      }
      return [];
    }
    const map = new Map();
    viewWorkspaces.forEach((ws) => {
      if (!ws.centreId && !ws.centreName) return;
      const key = ws.centreId || ws.centreName;
      if (!map.has(key)) {
        map.set(key, {
          centreId: ws.centreId,
          centreName: ws.centreName || `Centre #${ws.centreId}`,
          roles: ws.roleName ? [ws.roleName] : [],
          isDefault: !!ws.isDefault || (ws.centreId === viewEmployee?.centreId),
        });
      } else {
        const existing = map.get(key);
        if (ws.roleName && !existing.roles.includes(ws.roleName)) {
          existing.roles.push(ws.roleName);
        }
        if (ws.isDefault || ws.centreId === viewEmployee?.centreId) {
          existing.isDefault = true;
        }
      }
    });
    return Array.from(map.values());
  }, [viewWorkspaces, viewEmployee]);

  // Distinct roles across workspaces for View modal
  const viewDistinctRoles = useMemo(() => {
    const roles = new Set();
    if (viewEmployee?.roleName) roles.add(viewEmployee.roleName);
    if (Array.isArray(viewWorkspaces)) {
      viewWorkspaces.forEach((ws) => {
        if (ws.roleName) roles.add(ws.roleName);
      });
    }
    return Array.from(roles);
  }, [viewWorkspaces, viewEmployee]);

  // Form Validation
  const validateForm = () => {
    const errors = {};
    const fullName = (formData.firstName ? `${formData.firstName} ${formData.lastName || ''}`.trim() : formData.name?.trim()) || '';
    if (!fullName) errors.firstName = 'First name or full name is required.';

    if (!formData.mobile?.trim()) {
      errors.mobile = 'Mobile contact is required.';
    } else if (!/^\+?[0-9\s-]{7,15}$/.test(formData.mobile.trim())) {
      errors.mobile = 'Enter a valid phone number (7-15 digits).';
    }

    if (!formData.designation?.trim()) {
      errors.designation = 'Please select a Designation from Designation Master.';
    }

    if (!formData.username?.trim()) {
      errors.username = 'Username is required.';
    } else if (usernameAvailability === 'taken') {
      errors.username = 'Username is already taken by another staff member.';
    }

    if (formMode === 'create') {
      if (!formData.password || formData.password.length < 4) {
        errors.password = 'Password must be at least 4 characters long.';
      }
    } else {
      if (formData.password && formData.password.length < 4) {
        errors.password = 'New password must be at least 4 characters long.';
      }
    }

    if (selectedCentreIds.length === 0) {
      errors.centres = 'Please select at least one Diagnostic Centre.';
    }

    if (selectedRoleIds.length === 0) {
      errors.roles = 'Please select at least one Security Role.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit Add / Edit
  const handleSubmitForm = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateForm()) {
      showToast('Please fix the validation errors before saving.', 'error');
      return;
    }
    await performSave(null);
  };

  // Executes the actual create/update call. Re-invoked with a reAuthToken after the
  // admin completes step-up verification for sensitive field changes (mobile/email/PAN/password).
  const performSave = async (reAuthToken) => {
    setIsSubmitting(true);
    try {
      const resolvedName = (formData.firstName ? `${formData.firstName} ${formData.lastName || ''}`.trim() : formData.name?.trim()) || '';
      const primaryCentreId = selectedCentreIds[0] || 1;
      const primaryRoleId = selectedRoleIds[0] || 2;

      let savedEmpId = currentEmpId;

      if (formMode === 'create') {
        const payload = {
          title: formData.title,
          name: resolvedName,
          mobile: formData.mobile.trim(),
          email: formData.email?.trim() || null,
          dob: formData.dob || null,
          panNo: formData.panNo?.trim() || null,
          passportNo: formData.passportNo?.trim() || null,
          qualification: formData.qualification?.trim() || null,
          designation: formData.designation?.trim() || null,
          medicalRegNo: formData.medicalRegNo?.trim() || null,
          regAuthority: formData.regAuthority?.trim() || null,
          startDate: formData.startDate || null,
          username: formData.username.trim(),
          password: formData.password,
          passwordChangeFrequencyInDays: formData.passwordChangeFrequencyInDays ?? 0,
          isPasswordChangeRequired: !!formData.isPasswordChangeRequired,
          maxFailedLoginAttempts: formData.maxFailedLoginAttempts ?? 3,
          centreId: primaryCentreId,
          roleId: primaryRoleId,
          accessDepartment: formData.accessDepartment || null,
        };

        const res = await employeeService.createEmployee(payload);
        if (res.success) {
          savedEmpId = res.data?.employeeId;
          showToast(`Staff member "${payload.name}" successfully registered!`);
          if (savedEmpId) {
            setCurrentEmpId(savedEmpId);
            setFormMode('edit');
            fetchPasskeyCredentials(savedEmpId);
          }
          fetchEmployees();
        }
      } else {
        const payload = {
          title: formData.title,
          name: resolvedName,
          mobile: formData.mobile.trim(),
          email: formData.email?.trim() || null,
          dob: formData.dob || null,
          panNo: formData.panNo?.trim() || null,
          passportNo: formData.passportNo?.trim() || null,
          qualification: formData.qualification?.trim() || null,
          designation: formData.designation?.trim() || null,
          medicalRegNo: (formData.medicalRegNo || '').trim(),
          regAuthority: (formData.regAuthority || '').trim(),
          startDate: formData.startDate || null,
          username: formData.username.trim(),
          newPassword: formData.password ? formData.password : null,
          passwordChangeFrequencyInDays: formData.passwordChangeFrequencyInDays ?? 0,
          isPasswordChangeRequired: !!formData.isPasswordChangeRequired,
          maxFailedLoginAttempts: formData.maxFailedLoginAttempts ?? 3,
          centreId: primaryCentreId,
          roleId: primaryRoleId,
          accessDepartment: formData.accessDepartment || null,
          isLoginBlocked: !!formData.isLoginBlocked,
        };

        const res = await employeeService.updateEmployee(currentEmpId, payload, reAuthToken);
        if (res.success) {
          showToast(`Staff profile "${payload.name}" successfully updated!`);
          fetchEmployees();
        }
      }

      // Save multiple centres and multiple roles combinations into f_user_centre_role
      if (savedEmpId && selectedCentreIds.length > 0 && selectedRoleIds.length > 0) {
        const workspaces = [];
        selectedCentreIds.forEach((cId, cIdx) => {
          selectedRoleIds.forEach((rId, rIdx) => {
            workspaces.push({
              centreId: cId,
              roleId: rId,
              isDefault: cIdx === 0 && rIdx === 0,
            });
          });
        });
        try {
          await employeeService.saveUserWorkspaces(savedEmpId, workspaces);
        } catch (wsErr) {
          console.warn('Failed to save multiple user workspaces:', wsErr);
        }
      }

      // Save discount authority if configured in form
      if (savedEmpId && discountAuthorityDraft && discountAuthorityDraft.globalAuth) {
        try {
          const { globalAuth, centreAuthorities, approvalRules } = discountAuthorityDraft;
          const allAuthorities = [
            {
              ...globalAuth,
              employeeId: savedEmpId,
              centreId: null,
              maxDiscountPerBillPercent: parseFloat(globalAuth.maxDiscountPerBillPercent) || 0,
              maxDiscountPerBillAmount: parseFloat(globalAuth.maxDiscountPerBillAmount) || 0,
              dailyDiscountLimit: parseFloat(globalAuth.dailyDiscountLimit) || 0,
              monthlyDiscountLimit: parseFloat(globalAuth.monthlyDiscountLimit) || 0,
              yearlyDiscountLimit: parseFloat(globalAuth.yearlyDiscountLimit) || 0,
              ...refundFieldsForSave(globalAuth),
              selfApprovalMaxPercent: parseFloat(globalAuth.selfApprovalMaxPercent) || 0,
              selfApprovalMaxAmount: parseFloat(globalAuth.selfApprovalMaxAmount) || 0,
              validFrom: globalAuth.validFrom || null,
              validTo: globalAuth.validTo || null,
            },
            ...(centreAuthorities || []).map((c) => ({
              ...c,
              employeeId: savedEmpId,
              maxDiscountPerBillPercent: parseFloat(c.maxDiscountPerBillPercent) || 0,
              maxDiscountPerBillAmount: parseFloat(c.maxDiscountPerBillAmount) || 0,
              dailyDiscountLimit: parseFloat(c.dailyDiscountLimit) || 0,
              monthlyDiscountLimit: parseFloat(c.monthlyDiscountLimit) || 0,
              yearlyDiscountLimit: parseFloat(c.yearlyDiscountLimit) || 0,
              ...refundFieldsForSave(c),
              selfApprovalMaxPercent: parseFloat(c.selfApprovalMaxPercent) || 0,
              selfApprovalMaxAmount: parseFloat(c.selfApprovalMaxAmount) || 0,
              validFrom: c.validFrom || null,
              validTo: c.validTo || null,
            })),
          ];

          const cleanedRules = (approvalRules || []).map((r, idx) => ({
            ...r,
            employeeId: savedEmpId,
            levelNumber: idx + 1,
            minAmount: parseFloat(r.minAmount) || 0,
            maxAmount: parseFloat(r.maxAmount) || 99999999.99,
            approverRoleId: r.approverRoleId ? parseInt(r.approverRoleId, 10) : null,
            approverEmployeeId: r.approverEmployeeId ? parseInt(r.approverEmployeeId, 10) : null,
          }));

          await discountService.saveEmployeeAuthority(savedEmpId, {
            employeeId: savedEmpId,
            authorities: allAuthorities,
            rules: cleanedRules,
          });
        } catch (discErr) {
          console.warn('Failed to save discount authority for employee:', discErr);
        }
      }

      // Save access configuration (concurrent login limit, schedule restriction, timezone)
      if (savedEmpId) {
        try {
          await employeeService.saveAccessConfig(savedEmpId, {
            allowedConcurrentLogins: parseInt(accessConfig.allowedConcurrentLogins, 10) || 1,
            loginRestrictionEnabled: !!accessConfig.loginRestrictionEnabled,
            loginStartTime: accessConfig.loginStartTime,
            loginEndTime: accessConfig.loginEndTime,
            allowedDays: accessConfig.allowedDays,
            timezone: accessConfig.timezone,
            sessionTimeoutMinutes: parseInt(accessConfig.sessionTimeoutMinutes, 10) || 480,
            idleTimeoutMinutes: parseInt(accessConfig.idleTimeoutMinutes, 10) || 60,
          });
        } catch (accErr) {
          console.warn('Failed to save employee access configuration:', accErr);
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Operation failed.';
      const needsReAuth = err.response?.status === 403 && /re-authenticate/i.test(msg);
      if (needsReAuth && !reAuthToken) {
        // Backend requires step-up verification for sensitive field changes
        // (mobile/email/PAN/passport/password). Prompt the admin for their own
        // password, then retry this same save with the resulting token.
        setIsReAuthModalOpen(true);
      } else {
        showToast(msg, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Admin Quick Password Reset: uses the dedicated /auth/admin-reset-password endpoint,
  // which does NOT require the admin's own current password (unlike the main profile
  // save flow above) — this is the correct path when the employee's existing password
  // is unknown and you just need to set a temporary one and force a change at next login.
  const handleQuickPasswordReset = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!currentEmpId) return;
    if (!quickResetModal.newPassword || quickResetModal.newPassword.length < 4) {
      showToast('New password must be at least 4 characters long.', 'error');
      return;
    }

    setQuickResetModal((prev) => ({ ...prev, isSaving: true }));
    try {
      const res = await authService.adminResetPassword(
        currentEmpId,
        quickResetModal.newPassword,
        quickResetModal.forceChange
      );
      if (res.success) {
        showToast('Password reset successfully. The employee must set a new password at next login.');
        setQuickResetModal({ open: false, newPassword: '', forceChange: true, isSaving: false });
      } else {
        showToast(res.message || 'Failed to reset password.', 'error');
        setQuickResetModal((prev) => ({ ...prev, isSaving: false }));
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reset password.', 'error');
      setQuickResetModal((prev) => ({ ...prev, isSaving: false }));
    }
  };

  // Status toggle confirmation
  const handleConfirmStatusToggle = async () => {
    if (!statusModal.employee) return;
    const { employeeId, name, isActive } = statusModal.employee;
    const targetStatus = !isActive;

    setIsStatusToggling(true);
    try {
      const res = await employeeService.updateStatus(employeeId, targetStatus);
      if (res.success) {
        showToast(
          `Staff member "${name}" ${targetStatus ? 'activated' : 'deactivated (all active sessions revoked)'}.`
        );
        setStatusModal({ open: false, employee: null });
        fetchEmployees();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update employee status.', 'error');
    } finally {
      setIsStatusToggling(false);
    }
  };

  // Unlock an account locked by too many wrong passwords (resets the failed-attempt counter)
  const [unlockingId, setUnlockingId] = useState(null);
  const handleUnlockAccount = async (emp) => {
    setUnlockingId(emp.employeeId);
    try {
      const res = await employeeService.unlockAccount(emp.employeeId);
      if (res.success) {
        showToast(`Account of "${emp.name}" unlocked. They can sign in again.`);
        setViewEmployee((prev) => (prev && prev.employeeId === emp.employeeId ? { ...prev, invalidPasswordCount: 0, isAccountLocked: false } : prev));
        fetchEmployees();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to unlock account.', 'error');
    } finally {
      setUnlockingId(null);
    }
  };

  // Stats calculation
  const stats = useMemo(() => {
    const total = totalCount;
    const activeCount = employees.filter((e) => e.isActive).length;
    const inactiveCount = employees.filter((e) => !e.isActive).length;
    const doctors = employees.filter((e) => e.roleName?.toLowerCase().includes('pathologist') || e.roleName?.toLowerCase().includes('radiologist')).length;
    return { total, activeCount, inactiveCount, doctors };
  }, [employees, totalCount]);

  const getApprovalLabel = (lvl) => {
    switch (lvl) {
      case 1: return 'Level 1: Approve';
      case 2: return 'Level 2: Forward';
      case 3: return 'Level 3: Approve & Final Validation';
      case 4: return 'Level 4: Approve / Unhold / Final Validation';
      case 5: return 'Level 5: Result Entry';
      default: return 'None / Standard Access';
    }
  };

  const getCleanName = (emp) => {
    const title = (emp?.title || '').trim();
    let name = (emp?.name || '').trim();
    if (!name) return '—';
    if (title) {
      const rawTitle = title.replace('.', '').toLowerCase();
      const firstWord = name.split(/\s+/)[0]?.replace('.', '').toLowerCase();
      if (firstWord === rawTitle) {
        return name;
      }
      return `${title} ${name}`;
    }
    return name;
  };

  const getStaffInitials = (name = '') => {
    if (!name) return 'ST';
    const clean = name.replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, '').trim();
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return (clean.substring(0, 2) || 'ST').toUpperCase();
  };

  const getRoleVisual = (roleName = '', dept = '') => {
    const text = `${roleName || ''} ${dept || ''}`.toLowerCase();
    if (text.includes('patholog')) {
      return { bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe', icon: '🧪', label: roleName || 'Pathologist' };
    }
    if (text.includes('radiolog')) {
      return { bg: '#ecfeff', color: '#0891b2', border: '#a5f3fc', icon: '☢️', label: roleName || 'Radiologist' };
    }
    if (text.includes('tech')) {
      return { bg: '#fffbeb', color: '#b45309', border: '#fde68a', icon: '🔬', label: roleName || 'Lab Technician' };
    }
    if (text.includes('phleb')) {
      return { bg: '#f0fdfa', color: '#0d9488', border: '#99f6e4', icon: '💉', label: roleName || 'Phlebotomist' };
    }
    if (text.includes('admin') || text.includes('manager')) {
      return { bg: '#faf5ff', color: '#7e22ce', border: '#e9d5ff', icon: '🛡️', label: roleName || 'Administrator' };
    }
    if (text.includes('doc') || text.includes('consultant')) {
      return { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', icon: '👨‍⚕️', label: roleName || 'Doctor' };
    }
    return { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', icon: '👤', label: roleName || dept || 'Staff' };
  };

  return (
    <MasterPageWrapper
      masterTitle={showForm ? (formMode === 'create' ? 'Create Employee' : `Update Employee: ${formData.name || 'Staff'}`) : 'Employee Master'}
      masterName="Employee"
      viewMode={showForm ? 'form' : 'list'}
      onToggleView={(mode) => {
        if (mode === 'form' && formMode !== 'edit') handleOpenCreate();
        else setShowForm(mode === 'form');
      }}
    >
      {/* Toast Notification */}
      {toast && (
        <div className={`emp-toast ${toast.type === 'error' ? 'emp-toast-error' : 'emp-toast-success'}`} style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }}>
          <span>{toast.message}</span>
          <button className="emp-toast-close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      {/* ─── FULL-SCREEN FORM VIEW (FORM-FIRST EXPERIENCE) ─── */}
      {showForm ? (
        <div className="emp-form-container-clean fade-in">
          <form onSubmit={handleSubmitForm} noValidate>
            {/* 01. Personal Information & Identity */}
            <FormSection
              title="01. Personal Information & Identity"
              iconColor="#4287f5"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              }
            >
              <div className="emp-grid-4">
                <div className="emp-field-group">
                  <label className="emp-field-label">Title</label>
                  <select
                    className="emp-field-select"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    id="select-emp-title"
                  >
                    <option value="Mr.">Mr.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Dr.">Dr.</option>
                    <option value="Prof.">Prof.</option>
                  </select>
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">
                    First Name <span className="emp-field-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`emp-field-input ${formErrors.firstName ? 'emp-field-error-border' : ''}`}
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={(e) => {
                      const fName = e.target.value;
                      const lName = formData.lastName || '';
                      setFormData({
                        ...formData,
                        firstName: fName,
                        name: `${fName} ${lName}`.trim(),
                      });
                      if (formErrors.firstName) setFormErrors({ ...formErrors, firstName: null });
                    }}
                    id="input-emp-firstname"
                    required
                  />
                  {formErrors.firstName && <span className="emp-field-error-msg">{formErrors.firstName}</span>}
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Last Name</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={(e) => {
                      const lName = e.target.value;
                      const fName = formData.firstName || '';
                      setFormData({
                        ...formData,
                        lastName: lName,
                        name: `${fName} ${lName}`.trim(),
                      });
                    }}
                    id="input-emp-lastname"
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">
                    Mobile Phone <span className="emp-field-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`emp-field-input ${formErrors.mobile ? 'emp-field-error-border' : ''}`}
                    placeholder="e.g. 9876543210"
                    value={formData.mobile}
                    onChange={(e) => {
                      setFormData({ ...formData, mobile: e.target.value });
                      if (formErrors.mobile) setFormErrors({ ...formErrors, mobile: null });
                    }}
                    id="input-emp-mobile"
                    required
                  />
                  {formErrors.mobile && <span className="emp-field-error-msg">{formErrors.mobile}</span>}
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Email Address</label>
                  <input
                    type="email"
                    className="emp-field-input"
                    placeholder="e.g. employee@lims-diagnostic.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    id="input-emp-email"
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Date of Birth</label>
                  <input
                    type="date"
                    className="emp-field-input"
                    value={formData.dob}
                    onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">PAN Card Number</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="ABCDE1234F"
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    value={formData.panNo}
                    onChange={(e) => setFormData({ ...formData, panNo: e.target.value.toUpperCase() })}
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Passport Number</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="Passport No."
                    value={formData.passportNo || ''}
                    onChange={(e) => setFormData({ ...formData, passportNo: e.target.value })}
                  />
                </div>
              </div>
            </FormSection>

            {/* 02. Diagnostic & Professional Profile */}
            <FormSection
              title="02. Diagnostic & Professional Profile"
              iconColor="#10b981"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              }
            >
              <div className="emp-grid-4">
                <div className="emp-field-group">
                  <label className="emp-field-label">Employee Code</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    disabled
                    value={currentEmpId ? `EMP-${String(currentEmpId).padStart(4, '0')}` : 'Auto-generated on save'}
                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)', fontWeight: 600 }}
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">
                    Designation <span className="emp-field-req">*</span>
                  </label>
                  <select
                    className={`emp-field-select ${formErrors.designation ? 'emp-field-error-border' : ''}`}
                    value={formData.designation}
                    onChange={(e) => {
                      setFormData({ ...formData, designation: e.target.value });
                      if (formErrors.designation) setFormErrors({ ...formErrors, designation: null });
                    }}
                    id="select-emp-designation"
                    required
                  >
                    <option value="">-- Select Designation from Master --</option>
                    {designations.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                    {formData.designation &&
                      !designations.some((d) => d.name.toLowerCase() === formData.designation.toLowerCase()) && (
                        <option value={formData.designation}>{formData.designation}</option>
                      )}
                  </select>
                  {formErrors.designation && <span className="emp-field-error-msg">{formErrors.designation}</span>}
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Access Department</label>
                  <select
                    className="emp-field-select"
                    value={formData.accessDepartment}
                    onChange={(e) => setFormData({ ...formData, accessDepartment: e.target.value })}
                    id="select-emp-department"
                  >
                    <option value="">-- Select Department --</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.name}>
                        {dept.name} {dept.code ? `(${dept.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Joining Date</label>
                  <input
                    type="date"
                    className="emp-field-input"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    id="input-emp-startdate"
                  />
                </div>

                <div className="emp-field-group emp-col-full">
                  <label className="emp-field-label">Qualifications</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="e.g. MBBS, MD (Pathology), DNB"
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Medical Registration No. (doctors)</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="e.g. DMC-48291"
                    maxLength={50}
                    value={formData.medicalRegNo}
                    onChange={(e) => setFormData({ ...formData, medicalRegNo: e.target.value })}
                  />
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Registering Medical Council (doctors)</label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="e.g. Delhi Medical Council"
                    maxLength={100}
                    value={formData.regAuthority}
                    onChange={(e) => setFormData({ ...formData, regAuthority: e.target.value })}
                  />
                </div>
              </div>
            </FormSection>

            {/* 03. Authentication Credentials & Access Control */}
            <FormSection
              title="03. Authentication Credentials & Access Control"
              iconColor="#f59e0b"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
            >
              <div className="emp-grid-4">
                <div className="emp-field-group">
                  <label className="emp-field-label">
                    Username (System Login ID) <span className="emp-field-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`emp-field-input ${
                      formErrors.username || usernameAvailability === 'taken'
                        ? 'emp-field-error-border'
                        : ''
                    }`}
                    placeholder="e.g. ashanna"
                    value={formData.username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    id="input-emp-username"
                    required
                  />
                  {usernameAvailability === 'checking' && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Verifying username uniqueness...</span>
                  )}
                  {usernameAvailability === 'available' && (
                    <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>&#10004; Username is available!</span>
                  )}
                  {usernameAvailability === 'taken' && (
                    <span className="emp-field-error-msg">&#10008; Username already in use. Please choose another.</span>
                  )}
                  {formErrors.username && <span className="emp-field-error-msg">{formErrors.username}</span>}
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">
                    {formMode === 'create' ? 'Initial Password' : 'Change Password (Leave blank to keep current)'}{' '}
                    {formMode === 'create' && <span className="emp-field-req">*</span>}
                  </label>
                  <input
                    type="password"
                    className={`emp-field-input ${formErrors.password ? 'emp-field-error-border' : ''}`}
                    placeholder={formMode === 'create' ? 'Minimum 4 characters' : 'Enter new password or leave blank'}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      if (formErrors.password) setFormErrors({ ...formErrors, password: null });
                    }}
                    id="input-emp-password"
                  />
                  {formErrors.password && <span className="emp-field-error-msg">{formErrors.password}</span>}
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Primary Centre</label>
                  <select
                    className="emp-field-select"
                    value={formData.centreId}
                    onChange={(e) => setFormData({ ...formData, centreId: parseInt(e.target.value, 10) })}
                  >
                    {centres.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="emp-field-group">
                  <label className="emp-field-label">Primary Role</label>
                  <select
                    className="emp-field-select"
                    value={formData.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: parseInt(e.target.value, 10) })}
                  >
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                {/* Password Expiry Policy */}
                <div className="emp-field-group">
                  <label className="emp-field-label">Password Expiry Policy</label>
                  <select
                    className="emp-field-select"
                    value={formData.passwordChangeFrequencyInDays ?? 0}
                    onChange={(e) => setFormData({ ...formData, passwordChangeFrequencyInDays: parseInt(e.target.value, 10) })}
                    id="select-emp-pwd-expiry"
                  >
                    <option value={0}>Never (No Expiration)</option>
                    <option value={10}>10 Days</option>
                    <option value={20}>20 Days</option>
                    <option value={30}>30 Days</option>
                    <option value={40}>40 Days</option>
                  </select>
                </div>

                {/* Password Expiration Date (Calculated / Read-Only) */}
                <div className="emp-field-group">
                  <label className="emp-field-label">Password Expires On</label>
                  <div
                    className="emp-field-input"
                    style={{
                      backgroundColor: 'var(--bg-muted, #f8fafc)',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 600,
                      color: (formData.passwordChangeFrequencyInDays ?? 0) > 0 ? '#2563eb' : '#64748b'
                    }}
                  >
                    {(() => {
                      const freq = formData.passwordChangeFrequencyInDays ?? 0;
                      if (freq <= 0) return 'Never Expires';
                      const baseDate = formData.passwordLastChangedDate
                        ? new Date(formData.passwordLastChangedDate)
                        : new Date();
                      if (isNaN(baseDate.getTime())) return 'Never Expires';
                      const exp = new Date(baseDate.getTime() + freq * 24 * 60 * 60 * 1000);
                      return exp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    })()}
                  </div>
                </div>

                {/* Account lock after wrong passwords */}
                <div className="emp-field-group">
                  <label className="emp-field-label">Lock Account After Wrong Passwords</label>
                  <select
                    className="emp-field-select"
                    value={formData.maxFailedLoginAttempts ?? 3}
                    onChange={(e) => setFormData({ ...formData, maxFailedLoginAttempts: parseInt(e.target.value, 10) })}
                    id="select-emp-max-failed-logins"
                  >
                    <option value={3}>3 attempts (default)</option>
                    <option value={5}>5 attempts</option>
                    <option value={8}>8 attempts</option>
                    <option value={10}>10 attempts</option>
                    <option value={0}>Never lock</option>
                  </select>
                  {(formData.maxFailedLoginAttempts ?? 3) === 0 && (
                    <small style={{ color: '#b45309', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
                      Never lock allows unlimited password guesses. Use only when really needed.
                    </small>
                  )}
                </div>

                {/* Force Password Change on Next Login Checkbox */}
                <div className="emp-field-group" style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center' }}>
                  <label className="emp-admin-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={!!formData.isPasswordChangeRequired}
                      onChange={(e) => setFormData({ ...formData, isPasswordChangeRequired: e.target.checked })}
                      id="chk-emp-force-pwd-change"
                    />
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      Force Password Change on Next Login
                    </span>
                  </label>
                </div>

                {/* Administrative Account (Bypass Login / Lockout) */}
                <div className="emp-admin-lockout-banner">
                  <label className="emp-admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isLoginBlocked}
                      onChange={(e) => setFormData({ ...formData, isLoginBlocked: e.target.checked })}
                    />
                    <div className="emp-admin-checkbox-content">
                      <span className="emp-admin-title">
                        Administrative Account (Bypass Login) &mdash; IsLoginBlocked
                      </span>
                      <span className="emp-admin-desc">
                        Instantly blocks user from signing into LIS without deleting credentials or audit trail. Use only for admin purposes and not for regular user access.
                      </span>
                    </div>
                  </label>
                </div>

                {/* Sub-Card 1: Concurrent Login & Session Security Policy */}
                <div className="emp-policy-card">
                  <div className="emp-policy-card-header">
                    <div className="emp-policy-title-wrap">
                      <span className="emp-policy-icon">🛡️</span>
                      <div>
                        <h4 className="emp-policy-title">Concurrent Login &amp; Session Security Policy</h4>
                        <span className="emp-policy-subtitle">
                          Configure maximum allowed concurrent logins, session timeout and schedule restrictions.
                        </span>
                      </div>
                    </div>
                    <div className="emp-policy-header-actions">
                      {formMode === 'edit' && currentEmpId && (
                        <button
                          type="button"
                          className="emp-btn-live-sessions"
                          onClick={() => handleOpenSessionsModal({ employeeId: currentEmpId, name: formData.name || formData.username })}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                            <line x1="8" y1="21" x2="16" y2="21" />
                            <line x1="12" y1="17" x2="12" y2="21" />
                          </svg>
                          View Live Sessions
                        </button>
                      )}
                      <button
                        type="button"
                        className="emp-btn-toggle-details"
                        onClick={() => setShowSessionPolicyDetails(!showSessionPolicyDetails)}
                      >
                        {showSessionPolicyDetails ? '▲ View Less Details' : '▼ View Details'}
                      </button>
                    </div>
                  </div>

                  {showSessionPolicyDetails && (
                    <div className="emp-policy-card-body">
                      {/* Row 1: Allowed Concurrent Logins | Session Timeout | Idle Timeout */}
                      <div className="emp-grid-3">
                        <div className="emp-field-group">
                          <label className="emp-field-label">
                            Allowed Concurrent Logins <span className="emp-field-req">*</span>
                          </label>
                          <input
                            type="number"
                            className="emp-field-input"
                            min="1"
                            max="50"
                            value={accessConfig.allowedConcurrentLogins}
                            onChange={(e) => setAccessConfig({ ...accessConfig, allowedConcurrentLogins: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            id="input-allowed-concurrent-logins"
                          />
                          <span className="emp-field-hint">
                            Set the maximum number of simultaneous logins allowed (1, 2, 3 ... N).
                          </span>
                          <span className="emp-policy-overflow-note">
                            ℹ <strong>Overflow Policy:</strong> When exceeded, oldest active session is automatically revoked server-side.
                          </span>
                        </div>

                        <div className="emp-field-group">
                          <label className="emp-field-label">Session Timeout (Minutes)</label>
                          <input
                            type="number"
                            className="emp-field-input"
                            min="15"
                            value={accessConfig.sessionTimeoutMinutes}
                            onChange={(e) => setAccessConfig({ ...accessConfig, sessionTimeoutMinutes: parseInt(e.target.value, 10) || 480 })}
                            placeholder="480 (8 hrs)"
                          />
                          <span className="emp-field-hint">
                            Absolute max session duration (e.g. 480 = 8 hrs, 840 = 14 hrs).
                          </span>
                        </div>

                        <div className="emp-field-group">
                          <label className="emp-field-label">Idle Timeout</label>
                          <input
                            type="number"
                            className="emp-field-input"
                            min="5"
                            value={accessConfig.idleTimeoutMinutes}
                            onChange={(e) => setAccessConfig({ ...accessConfig, idleTimeoutMinutes: parseInt(e.target.value, 10) || 60 })}
                            placeholder="60 (1 hr)"
                          />
                          <span className="emp-field-hint">
                            Inactivity auto-lock threshold (e.g. 60 min).
                          </span>
                        </div>
                      </div>

                      {/* Schedule Restrictions Toggle */}
                      <div className="emp-schedule-toggle-row">
                        <label className="emp-checkbox-label">
                          <input
                            type="checkbox"
                            checked={accessConfig.loginRestrictionEnabled}
                            onChange={(e) => setAccessConfig({ ...accessConfig, loginRestrictionEnabled: e.target.checked })}
                            id="toggle-login-restriction"
                          />
                          <div>
                            <span className="emp-schedule-toggle-title">
                              ENABLE WORK SHIFT / SCHEDULE RESTRICTIONS [SERVER-AUTHORITATIVE TIME CHECK]
                            </span>
                            <span className="emp-schedule-toggle-desc">
                              Restrict user login to configured working hours and days. Supports overnight shifts (e.g. 22:00 to 06:00).
                            </span>
                          </div>
                        </label>
                      </div>

                      {/* Schedule Controls (when enabled) */}
                      {accessConfig.loginRestrictionEnabled && (
                        <div className="emp-schedule-panel">
                          {/* Row 2: Start Time | End Time | Timezone */}
                          <div className="emp-grid-3">
                            <div className="emp-field-group">
                              <label className="emp-field-label">Start Time (HH:MM)</label>
                              <input
                                type="time"
                                step="1"
                                className="emp-field-input"
                                value={accessConfig.loginStartTime}
                                onChange={(e) => setAccessConfig({ ...accessConfig, loginStartTime: e.target.value ? (e.target.value.length === 5 ? `${e.target.value}:00` : e.target.value) : '08:00:00' })}
                                id="input-login-start-time"
                              />
                            </div>
                            <div className="emp-field-group">
                              <label className="emp-field-label">End Time (HH:MM)</label>
                              <input
                                type="time"
                                step="1"
                                className="emp-field-input"
                                value={accessConfig.loginEndTime}
                                onChange={(e) => setAccessConfig({ ...accessConfig, loginEndTime: e.target.value ? (e.target.value.length === 5 ? `${e.target.value}:00` : e.target.value) : '18:00:00' })}
                                id="input-login-end-time"
                              />
                            </div>
                            <div className="emp-field-group">
                              <label className="emp-field-label">Timezone</label>
                              <select
                                className="emp-field-select"
                                value={accessConfig.timezone}
                                onChange={(e) => setAccessConfig({ ...accessConfig, timezone: e.target.value })}
                                id="select-login-timezone"
                              >
                                <option value="Asia/Kolkata">Asia/Kolkata (GMT +5:30)</option>
                                <option value="UTC">UTC (Universal Time)</option>
                                <option value="Asia/Dubai">Asia/Dubai (GMT +4:00)</option>
                                <option value="America/New_York">America/New_York (EST/EDT)</option>
                                <option value="Europe/London">Europe/London (GMT/BST)</option>
                                <option value="Asia/Singapore">Asia/Singapore (SGT +8:00)</option>
                              </select>
                            </div>
                          </div>

                          {/* Row 3: Allowed Working Days */}
                          <div className="emp-working-days-group">
                            <label className="emp-working-days-label">ALLOWED WORKING DAYS</label>
                            <div className="emp-working-days-pills">
                              {DAYS_OF_WEEK.map((day) => {
                                const isSelected = accessConfig.allowedDays.includes(day.code);
                                return (
                                  <button
                                    key={day.code}
                                    type="button"
                                    className={`emp-day-pill ${isSelected ? 'emp-day-pill-selected' : ''}`}
                                    onClick={() => {
                                      const next = isSelected
                                        ? accessConfig.allowedDays.filter((d) => d !== day.code)
                                        : [...accessConfig.allowedDays, day.code];
                                      setAccessConfig({ ...accessConfig, allowedDays: next });
                                    }}
                                  >
                                    {isSelected && <span className="emp-day-check">✓</span>}
                                    {day.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sub-Card 2: Authentication & Security Controls */}
                <div className="emp-security-module">
                  <div className="emp-security-module-header">
                    <div className="emp-security-header-title">
                      <span className="emp-sec-item-icon" style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>🛡️</span>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          Authentication &amp; Security
                        </h4>
                        <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                          Manage login credentials, security settings and access permissions.
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="emp-security-rows-list">
                    {/* Method 1: Password & Login */}
                    <div className="emp-sec-item-row">
                      <div className="emp-sec-item-left">
                        <span className="emp-sec-item-icon" style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>🔑</span>
                        <div>
                          <div className="emp-sec-item-name">Password &amp; Login</div>
                          <div className="emp-sec-item-desc">Reset login password or configure password policies.</div>
                        </div>
                      </div>
                      <div className="emp-sec-item-right">
                        <span className="emp-status-badge emp-status-active">
                          <span className="emp-status-dot"></span> Enabled
                        </span>
                        <button
                          type="button"
                          className="emp-btn-outline-sec"
                          disabled={formMode !== 'edit' || !currentEmpId}
                          title={formMode !== 'edit' || !currentEmpId ? 'Save the employee first to enable password reset' : 'Reset this employee\'s login password'}
                          onClick={() => setQuickResetModal({ open: true, newPassword: '', forceChange: true, isSaving: false })}
                        >
                          ⚙ MANAGE
                        </button>
                      </div>
                    </div>

                    {/* Method 2: Session Login / Inactivity */}
                    <div className="emp-sec-item-row">
                      <div className="emp-sec-item-left">
                        <span className="emp-sec-item-icon" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>🛡️</span>
                        <div>
                          <div className="emp-sec-item-name">Session Login / Inactivity</div>
                          <div className="emp-sec-item-desc">Manage session timeout, concurrent sessions and inactivity rules.</div>
                        </div>
                      </div>
                      <div className="emp-sec-item-right">
                        <span className="emp-status-badge emp-status-active">
                          <span className="emp-status-dot"></span> Active Policy
                        </span>
                        {formMode === 'edit' && currentEmpId && (
                          <button
                            type="button"
                            className="emp-btn-danger-outline"
                            onClick={() => handleOpenSessionsModal({ employeeId: currentEmpId, name: formData.name || formData.username })}
                          >
                            🔄 RESET INACTIVITY
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Method 3: Register Biometric Login */}
                    <div className="emp-sec-item-row">
                      <div className="emp-sec-item-left">
                        <span className="emp-sec-item-icon" style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>🖐</span>
                        <div>
                          <div className="emp-sec-item-name">Register Biometric Login</div>
                          <div className="emp-sec-item-desc">Configure biometric device access (Windows Hello, Fingerprint, WebAuthn Passkeys).</div>
                        </div>
                      </div>
                      <div className="emp-sec-item-right">
                        {passkeyCredentials && passkeyCredentials.length > 0 ? (
                          <span className="emp-status-badge emp-status-active">
                            <span className="emp-status-dot"></span> Registered ({passkeyCredentials.length})
                          </span>
                        ) : (
                          <span className="emp-status-badge emp-status-inactive">
                            <span className="emp-status-dot"></span> Not Registered
                          </span>
                        )}
                        {currentEmpId ? (
                          <button
                            type="button"
                            className="emp-btn-outline-primary"
                            onClick={() => {
                              setPasskeyError(null);
                              setShowPasskeyModal(true);
                            }}
                            disabled={isRegisteringPasskey || isLoadingPasskeys}
                            id="btn-register-passkey"
                          >
                            <span>🖐</span>
                            <span>
                              {passkeyCredentials && passkeyCredentials.length > 0
                                ? '+ REGISTER ANOTHER'
                                : 'REGISTER'}
                            </span>
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            Save employee first to enable biometric registration.
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Face Biometric Enrollment Card */}
                    <div className="emp-sec-item" style={{ marginTop: '12px', borderTop: '1px dashed #e2e8f0', paddingTop: '12px' }}>
                      <div className="emp-sec-item-left">
                        <div className="emp-sec-item-icon">👤</div>
                        <div>
                          <div className="emp-sec-item-name">Facial Biometrics</div>
                          <div className="emp-sec-item-desc">Camera-based facial recognition login with AI liveness verification.</div>
                        </div>
                      </div>
                      <div className="emp-sec-item-right">
                        {faceBiometricStatus.isEnrolled ? (
                          <span className="emp-status-badge emp-status-active">
                            <span className="emp-status-dot"></span> Enrolled {faceBiometricStatus.quality ? `(${Math.round(faceBiometricStatus.quality * 100)}%)` : ''}
                          </span>
                        ) : (
                          <span className="emp-status-badge emp-status-inactive">
                            <span className="emp-status-dot"></span> Not Enrolled
                          </span>
                        )}
                        {currentEmpId ? (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              className="emp-btn-outline-primary"
                              onClick={() => setShowFaceModal(true)}
                              id="btn-enroll-face"
                            >
                              <span>📷</span>
                              <span>{faceBiometricStatus.isEnrolled ? 'RE-ENROLL FACE' : 'ENROLL FACE'}</span>
                            </button>
                            {faceBiometricStatus.isEnrolled && (
                              <button
                                type="button"
                                className="emp-btn-revoke"
                                onClick={handleRevokeFaceBiometric}
                                title="Revoke facial biometrics"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            Save employee first to enroll face.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Registered Devices List */}
                  {currentEmpId && passkeyCredentials && passkeyCredentials.length > 0 && (
                    <div className="emp-registered-credentials-box">
                      <div className="emp-registered-credentials-title">
                        Registered Authenticator Devices ({passkeyCredentials.length}):
                      </div>
                      {passkeyCredentials.map((cred) => (
                        <div key={cred.id} className="emp-credential-item-clean">
                          <div className="emp-cred-info">
                            <span className="emp-cred-icon">💻</span>
                            <div>
                              <div className="emp-cred-name">{cred.deviceFriendlyName || 'Staff Workstation'}</div>
                              <div className="emp-cred-meta">
                                Status: <strong style={{ color: '#16a34a' }}>Active</strong> &bull; Registered: {cred.createdAt ? new Date(cred.createdAt).toLocaleDateString() : 'Active'} &bull; Sign Count: {cred.signCount || 0}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="emp-btn-revoke"
                            onClick={() => handleRevokePasskey(cred.id, cred.deviceFriendlyName)}
                            title="Revoke this device"
                          >
                            Revoke
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </FormSection>

            {/* 04. Center & Role Assignments (Multi-Facility Authorization) */}
            <FormSection
              title="04. Center & Role Assignments (Multi-Facility Authorization)"
              iconColor="#8b5cf6"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              }
            >
              <div className="emp-grid-2">
                <MultiSelectDropdown
                  label="Diagnostic Centre Access"
                  placeholder="+ Select Diagnostic Centres..."
                  searchPlaceholder="Search centres by name or code..."
                  options={centres}
                  selectedIds={selectedCentreIds}
                  onChange={(newIds) => {
                    setSelectedCentreIds(newIds);
                    if (formErrors.centres) setFormErrors({ ...formErrors, centres: null });
                  }}
                  required={true}
                  error={formErrors.centres}
                  chipClass="emp-chip-centre"
                  id="multiselect-emp-centres"
                  actionButtonText="+ Add Business"
                />

                <MultiSelectDropdown
                  label="Role Access"
                  placeholder="+ Select Operational Roles..."
                  searchPlaceholder="Search security roles..."
                  options={roles}
                  selectedIds={selectedRoleIds}
                  onChange={(newIds) => {
                    setSelectedRoleIds(newIds);
                    if (formErrors.roles) setFormErrors({ ...formErrors, roles: null });
                  }}
                  required={true}
                  error={formErrors.roles}
                  chipClass="emp-chip-role"
                  id="multiselect-emp-roles"
                  actionButtonText="+ Add Role/Access"
                />
              </div>
            </FormSection>

            {/* 05. Discount & Approval Authority */}
            <FormSection
              title="05. Discount & Approval Authority (Tariff Limits & Approval Model)"
              iconColor="#ec4899"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                  <path d="M12 6v2m0 8v2" />
                </svg>
              }
            >
              <DiscountAuthoritySection
                employeeId={currentEmpId}
                centres={centres}
                roles={roles}
                showToast={showToast}
                onDataChange={setDiscountAuthorityDraft}
              />
            </FormSection>

            {/* Form Footer Actions */}
            <div className="emp-form-actions-clean">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleResetForm}
                id="btn-reset-form"
              >
                Reset Form
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
                id="btn-view-grid-bottom"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || usernameAvailability === 'taken'}
                id="btn-save-employee-bottom"
                style={{ minWidth: '150px' }}
              >
                {isSubmitting ? 'Saving...' : formMode === 'create' ? 'Save Employee' : 'Update Employee'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* ─── FULL-SCREEN GRID / LIST VIEW ─── */
        <div className="emp-directory-container fade-in">
          {/* Toolbar Filters & Search */}
          <div className="emp-filter-card">
            <div className="emp-filter-controls">
              <div className="emp-search-wrap">
                <svg className="emp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  className="emp-search-input"
                  placeholder="Search by staff name, department, role, username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  id="emp-search-input"
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="emp-search-clear"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              <select
                className="emp-filter-select"
                style={{ minWidth: '180px' }}
                value={selectedRoleId}
                onChange={(e) => { setSelectedRoleId(e.target.value); setPage(1); }}
                id="select-filter-role"
              >
                <option value="">All Roles / Depts</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>

              <select
                className="emp-filter-select"
                style={{ minWidth: '180px' }}
                value={selectedCentreId}
                onChange={(e) => { setSelectedCentreId(e.target.value); setPage(1); }}
                id="select-filter-centre"
              >
                <option value="">All Centres / Cities</option>
                {centres.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <select
                className="emp-filter-select"
                style={{ minWidth: '140px' }}
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
                id="select-filter-status"
              >
                <option value="">All Statuses</option>
                <option value="true">Active Only</option>
                <option value="false">Inactive Only</option>
              </select>

              {(searchTerm || selectedRoleId || selectedCentreId || selectedStatus) && (
                <button
                  type="button"
                  className="emp-btn-reset-filter"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedRoleId('');
                    setSelectedCentreId('');
                    setSelectedStatus('');
                    setPage(1);
                  }}
                  id="btn-clear-emp-filters"
                >
                  Clear Filters
                </button>
              )}
            </div>

            <div className="emp-stats-summary">
              <span>Showing {employees.length} of {totalCount} records</span>
              <span className="emp-badge-count">{stats.activeCount} Active</span>
            </div>
          </div>

          {/* Table Card */}
          <div className="emp-table-card">
            <div className="emp-table-wrapper">
              {isLoading ? (
                <div style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div className="spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                  <p style={{ fontWeight: 500, fontSize: '0.9rem' }}>Loading employee directory and authorizations...</p>
                </div>
              ) : employees.length === 0 ? (
                <div style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.75rem' }}>👥</span>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontWeight: 700 }}>No Employees Found</h4>
                  <p style={{ fontSize: '0.85rem', margin: 0 }}>No employee records matched your filter criteria.</p>
                </div>
              ) : (
                <table className="emp-modern-table" id="emp-data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                      <th style={{ minWidth: '220px' }}>Staff Member</th>
                      <th style={{ width: '170px' }}>Department / Role</th>
                      <th style={{ width: '190px' }}>Centre / City</th>
                      <th style={{ width: '120px' }}>PAN Number</th>
                      <th style={{ width: '120px' }}>Username</th>
                      <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                      <th style={{ width: '210px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp, index) => {
                      const sNo = (page - 1) * pageSize + index + 1;
                      const cleanName = getCleanName(emp);
                      const initials = getStaffInitials(cleanName);
                      const roleVisual = getRoleVisual(emp.roleName, emp.department);
                      const pan = emp.panNo || emp.panNumber;
                      const centreDisplay = emp.centreName || emp.city;

                      return (
                        <tr key={emp.employeeId}>
                          <td className="emp-sno-col">{sNo}</td>
                          <td>
                            <div className="emp-staff-profile-cell">
                              <div
                                className="emp-staff-avatar"
                                style={{
                                  backgroundColor: roleVisual.bg,
                                  color: roleVisual.color,
                                  border: `1px solid ${roleVisual.border}`
                                }}
                              >
                                {initials}
                              </div>
                              <div className="emp-staff-info">
                                <div className="emp-staff-name">{cleanName}</div>
                                <div className="emp-staff-designation">
                                  {emp.designation || emp.roleName || 'Staff Member'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className="emp-role-pill"
                              style={{
                                backgroundColor: roleVisual.bg,
                                color: roleVisual.color,
                                borderColor: roleVisual.border
                              }}
                            >
                              <span>{roleVisual.icon}</span>
                              <span>{roleVisual.label}</span>
                            </span>
                          </td>
                          <td>
                            {centreDisplay ? (
                              <div className="emp-centre-chip" title={centreDisplay}>
                                <span>🏢</span>
                                <span>{centreDisplay}</span>
                              </div>
                            ) : (
                              <span className="emp-empty-dash">—</span>
                            )}
                          </td>
                          <td>
                            {pan ? (
                              <span className="emp-mono-chip">{pan}</span>
                            ) : (
                              <span className="emp-empty-dash">—</span>
                            )}
                          </td>
                          <td>
                            {emp.userName ? (
                              <span className="emp-user-handle">@{emp.userName}</span>
                            ) : (
                              <span className="emp-empty-dash">—</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`emp-modern-status-pill ${emp.isActive ? 'active' : 'inactive'}`}>
                              <span className="emp-status-dot"></span>
                              <span>{emp.isActive ? 'Active' : 'Inactive'}</span>
                            </span>
                            {emp.isAccountLocked && (
                              <div style={{ marginTop: 4 }}>
                                <span
                                  title="Locked after too many wrong passwords"
                                  style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 999, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}
                                >
                                  🔒 Locked
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="emp-actions-wrap">
                              <button
                                type="button"
                                className="emp-action-btn-clean emp-btn-view"
                                onClick={() => handleOpenView(emp.employeeId)}
                                title="View Details"
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                                <span>View</span>
                              </button>
                              <button
                                type="button"
                                className="emp-action-btn-clean emp-btn-edit"
                                onClick={() => handleOpenEdit(emp.employeeId)}
                                title="Edit Employee"
                                id={`btn-edit-${emp.employeeId}`}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                                <span>Edit</span>
                              </button>
                              <button
                                type="button"
                                className="emp-action-btn-clean"
                                style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}
                                onClick={() => handleOpenSessionsModal(emp)}
                                title="View Active Sessions & Connected Devices"
                                id={`btn-sessions-${emp.employeeId}`}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                  <line x1="8" y1="21" x2="16" y2="21" />
                                  <line x1="12" y1="17" x2="12" y2="21" />
                                </svg>
                                <span>Sessions</span>
                              </button>
                              {emp.isAccountLocked && (
                                <button
                                  type="button"
                                  className="emp-action-btn-clean"
                                  style={{ background: '#fffbeb', color: '#b45309', borderColor: '#fde68a' }}
                                  onClick={() => handleUnlockAccount(emp)}
                                  disabled={unlockingId === emp.employeeId}
                                  title="Unlock this account so the employee can sign in again"
                                  id={`btn-unlock-${emp.employeeId}`}
                                >
                                  <span>{unlockingId === emp.employeeId ? 'Unlocking…' : '🔓 Unlock'}</span>
                                </button>
                              )}
                              <button
                                type="button"
                                className={`emp-action-btn-clean ${emp.isActive ? 'emp-btn-deactivate' : 'emp-btn-activate'}`}
                                onClick={() => setStatusModal({ open: true, employee: emp })}
                                title={emp.isActive ? 'Deactivate Employee' : 'Activate Employee'}
                              >
                                {emp.isActive ? (
                                  <>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10" />
                                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                    </svg>
                                    <span>Deactivate</span>
                                  </>
                                ) : (
                                  <>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    <span>Activate</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Centralized Pagination */}
            <div style={{ padding: '8px 16px', borderTop: '1px solid #f1f5f9' }}>
              <CommonPagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalCount}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                }}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          VIEW EMPLOYEE PROFILE MODAL
         ===================================================================== */}
      {isViewModalOpen && (
        <div className="emp-modal-overlay">
          <div className="emp-modal-container">
            {isViewLoading || !viewEmployee ? (
              <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                <div className="lis-spinner" style={{ margin: '0 auto 16px' }}></div>
                <p style={{ color: '#64748b', fontSize: '0.95rem', fontWeight: 500 }}>
                  Loading staff credentials and profile...
                </p>
              </div>
            ) : (
              <>
                {/* ─── Profile Header Banner ─── */}
                <div className="emp-profile-header-banner">
                  <div className="emp-profile-header-left">
                    <div className="emp-profile-avatar-wrap">
                      <div className="emp-profile-avatar">
                        {getStaffInitials(viewEmployee.name)}
                      </div>
                      <span className={`emp-avatar-status-dot ${viewEmployee.isActive ? 'active' : 'inactive'}`} />
                    </div>

                    <div className="emp-profile-title-col">
                      <div className="emp-profile-name-row">
                        <h2 className="emp-profile-fullname">
                          {getCleanName(viewEmployee)}
                        </h2>
                        <span className={`emp-badge-status ${viewEmployee.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {viewEmployee.isActive ? '● Active Staff' : '○ Inactive / Deactivated'}
                        </span>
                        {viewEmployee.isLoginBlocked && (
                          <span className="emp-badge-status badge-blocked">
                            ⚠️ Login Blocked
                          </span>
                        )}
                      </div>

                      <div className="emp-profile-submeta">
                        <span className="emp-profile-designation">
                          {(viewEmployee.designation || 'Staff Member').toUpperCase()}
                        </span>
                        <span className="emp-meta-divider">•</span>
                        <span className="emp-profile-center">
                          🏥 {viewEmployee.centreName || 'Primary Diagnostic Hub'}
                        </span>
                        {viewEmployee.accessDepartment && (
                          <>
                            <span className="emp-meta-divider">•</span>
                            <span className="emp-profile-dept">
                              🔬 {viewEmployee.accessDepartment}
                            </span>
                          </>
                        )}
                        {viewEmployee.roleName && (
                          <>
                            <span className="emp-meta-divider">•</span>
                            <span className="emp-profile-role-tag">
                              🛡️ {viewEmployee.roleName}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="emp-profile-header-actions">
                    <button
                      type="button"
                      className="emp-btn-header-edit"
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleOpenEdit(viewEmployee.employeeId);
                      }}
                      title="Edit Staff Member"
                      id="btn-header-edit-profile"
                    >
                      <span>✎</span> Edit Profile
                    </button>
                    <button
                      type="button"
                      className="emp-modal-close"
                      onClick={() => setIsViewModalOpen(false)}
                      aria-label="Close modal"
                      title="Close"
                      id="btn-close-view-modal"
                    >
                      &times;
                    </button>
                  </div>
                </div>

                {/* ─── Scrollable Profile Body ─── */}
                <div className="emp-modal-body-scrollable">
                  <div className="emp-profile-content-grid">
                    {/* Section 1: Personal Details */}
                    <div className="emp-info-card">
                      <div className="emp-info-card-header">
                        <div className="emp-info-card-title-group">
                          <span className="emp-info-card-icon">👤</span>
                          <h3 className="emp-info-card-title">Personal Information</h3>
                        </div>
                        <span className="emp-info-card-badge">Identity</span>
                      </div>
                      <div className="emp-info-grid-3">
                        <div className="emp-info-field">
                          <span className="emp-info-label">Title</span>
                          <span className="emp-info-value">{viewEmployee.title || 'Mr.'}</span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Mobile Phone</span>
                          <span className={`emp-info-value ${!viewEmployee.mobile ? 'empty' : ''}`}>
                            {viewEmployee.mobile || 'Not Provided'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Email Address</span>
                          <span className={`emp-info-value ${!viewEmployee.email ? 'empty' : ''}`}>
                            {viewEmployee.email || 'Not Provided'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Date of Birth</span>
                          <span className={`emp-info-value ${!viewEmployee.dob ? 'empty' : ''}`}>
                            {viewEmployee.dob ? viewEmployee.dob.split('T')[0] : '—'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">PAN Number</span>
                          <span className={`emp-info-value font-mono ${!viewEmployee.panNo ? 'empty' : ''}`}>
                            {viewEmployee.panNo || '—'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Passport No.</span>
                          <span className={`emp-info-value font-mono ${!viewEmployee.passportNo ? 'empty' : ''}`}>
                            {viewEmployee.passportNo || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Professional Details */}
                    <div className="emp-info-card">
                      <div className="emp-info-card-header">
                        <div className="emp-info-card-title-group">
                          <span className="emp-info-card-icon">💼</span>
                          <h3 className="emp-info-card-title">Professional Details</h3>
                        </div>
                        <span className="emp-info-card-badge">Clinical &amp; Staff</span>
                      </div>
                      <div className="emp-info-grid">
                        <div className="emp-info-field">
                          <span className="emp-info-label">Employee Code</span>
                          <span className="emp-info-value font-mono">
                            {viewEmployee.employee_Code || `EMP-${String(viewEmployee.employeeId).padStart(4, '0')}`}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Designation</span>
                          <span className="emp-info-value">{viewEmployee.designation || 'Staff'}</span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Department</span>
                          <span className="emp-info-value">{viewEmployee.accessDepartment || 'All Departments'}</span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Primary Role</span>
                          <span className="emp-info-value">{viewEmployee.roleName || 'Staff'}</span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Joining Date</span>
                          <span className={`emp-info-value ${!viewEmployee.startDate ? 'empty' : ''}`}>
                            {viewEmployee.startDate ? viewEmployee.startDate.split('T')[0] : '—'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Qualifications</span>
                          <span className={`emp-info-value ${!viewEmployee.qualification ? 'empty' : ''}`}>
                            {viewEmployee.qualification || '—'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Medical Registration</span>
                          <span className={`emp-info-value ${!viewEmployee.medicalRegNo ? 'empty' : ''}`}>
                            {viewEmployee.medicalRegNo ? `${viewEmployee.medicalRegNo}${viewEmployee.regAuthority ? ` (${viewEmployee.regAuthority})` : ''}` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Account & Security */}
                    <div className="emp-info-card">
                      <div className="emp-info-card-header">
                        <div className="emp-info-card-title-group">
                          <span className="emp-info-card-icon">🔒</span>
                          <h3 className="emp-info-card-title">Account &amp; Security</h3>
                        </div>
                        <span className="emp-info-card-badge">Authentication</span>
                      </div>
                      <div className="emp-info-grid">
                        <div className="emp-info-field">
                          <span className="emp-info-label">Login Username</span>
                          <span className="emp-info-value font-mono" style={{ fontWeight: 700 }}>
                            {viewEmployee.userName || viewEmployee.username || '—'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Account Status</span>
                          <span className="emp-info-value">
                            <span className={`emp-badge-status ${viewEmployee.isActive ? 'badge-active' : 'badge-inactive'}`}>
                              {viewEmployee.isActive ? '● Normal Access' : '○ Account Deactivated'}
                            </span>
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Account Lockout</span>
                          <span className="emp-info-value">
                            {viewEmployee.isLoginBlocked ? (
                              <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ Locked / Blocked</span>
                            ) : (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Normal Access</span>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Security Hash</span>
                          <span className="emp-info-value font-mono" style={{ color: '#0369a1', fontSize: '0.82rem' }}>
                            🛡️ BCrypt Protected
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Passkey / Biometric</span>
                          <span className="emp-info-value">
                            {viewEmployeePasskeys && viewEmployeePasskeys.length > 0 ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>
                                ✔ Registered ({viewEmployeePasskeys.length} Device{viewEmployeePasskeys.length > 1 ? 's' : ''})
                              </span>
                            ) : (
                              <span className="emp-info-value empty">Not Registered</span>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Facial Recognition</span>
                          <span className="emp-info-value">
                            {viewEmployeeFace && viewEmployeeFace.isEnrolled ? (
                              <span style={{ color: '#0284c7', fontWeight: 600 }}>
                                ✔ Enrolled {viewEmployeeFace.quality ? `(${Math.round(viewEmployeeFace.quality * 100)}% Quality)` : ''}
                              </span>
                            ) : (
                              <span className="emp-info-value empty">Not Enrolled</span>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Failed Password Count</span>
                          <span className="emp-info-value font-mono">
                            {viewEmployee.invalidPasswordCount || 0} of {viewEmployee.maxFailedLoginAttempts === 0 ? 'unlimited' : (viewEmployee.maxFailedLoginAttempts ?? 3)} Attempts
                            {viewEmployee.isAccountLocked && (
                              <button
                                type="button"
                                className="emp-action-btn-clean"
                                style={{ marginLeft: 10, background: '#fffbeb', color: '#b45309', borderColor: '#fde68a' }}
                                onClick={() => handleUnlockAccount(viewEmployee)}
                                disabled={unlockingId === viewEmployee.employeeId}
                              >
                                {unlockingId === viewEmployee.employeeId ? 'Unlocking…' : '🔓 Unlock Account'}
                              </button>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Last Login Time</span>
                          <span className={`emp-info-value ${!viewEmployee.lastLoginTime ? 'empty' : ''}`}>
                            {viewEmployee.lastLoginTime ? viewEmployee.lastLoginTime.replace('T', ' ').substring(0, 19) : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Section 4: Roles & Access */}
                    <div className="emp-info-card">
                      <div className="emp-info-card-header">
                        <div className="emp-info-card-title-group">
                          <span className="emp-info-card-icon">🛡️</span>
                          <h3 className="emp-info-card-title">Roles &amp; Access</h3>
                        </div>
                        <span className="emp-info-card-badge">Permissions</span>
                      </div>
                      <div className="emp-info-grid" style={{ marginBottom: '10px' }}>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Primary Role</span>
                          <span className="emp-info-value">{viewEmployee.roleName || 'Staff'}</span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Access Department</span>
                          <span className="emp-info-value">{viewEmployee.accessDepartment || 'All Departments'}</span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Clinical Approval</span>
                          <span className="emp-info-value" style={{ color: '#0369a1', fontWeight: 700 }}>
                            {getApprovalLabel(viewEmployee.approvalLevel)}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Assigned Roles</span>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                            {viewDistinctRoles.map((r, i) => (
                              <span key={i} className="emp-profile-role-tag">
                                {r}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span className="emp-info-label" style={{ display: 'block', marginBottom: '6px' }}>
                          Diagnostic Privileges &amp; Controls
                        </span>
                        <div className="emp-privilege-chips">
                          <span className={`emp-privilege-chip ${viewEmployee.globalReportAccess ? 'enabled' : ''}`}>
                            {viewEmployee.globalReportAccess ? '✔' : '○'} Global Report Access
                          </span>
                          <span className={`emp-privilege-chip ${viewEmployee.isEditMacReading ? 'enabled' : ''}`}>
                            {viewEmployee.isEditMacReading ? '✔' : '○'} Edit Analyzer Readings
                          </span>
                          <span className={`emp-privilege-chip ${viewEmployee.amrValueAccess ? 'enabled' : ''}`}>
                            {viewEmployee.amrValueAccess ? '✔' : '○'} AMR Value Access
                          </span>
                          <span className={`emp-privilege-chip ${viewEmployee.allowWorksheet ? 'enabled' : ''}`}>
                            {viewEmployee.allowWorksheet ? '✔' : '○'} Worksheet Access
                          </span>
                          {viewEmployee.isSalesTeamMember && (
                            <span className="emp-privilege-chip enabled">
                              ✔ Sales Team Member
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 5: Authorized Diagnostic Centers */}
                    <div className="emp-info-card emp-profile-col-full">
                      <div className="emp-info-card-header">
                        <div className="emp-info-card-title-group">
                          <span className="emp-info-card-icon">🏥</span>
                          <h3 className="emp-info-card-title">
                            Authorized Diagnostic Centers ({viewDistinctCentres.length})
                          </h3>
                        </div>
                        <span className="emp-info-card-badge">Facility Access</span>
                      </div>
                      <div className="emp-centers-grid">
                        {viewDistinctCentres.length > 0 ? (
                          viewDistinctCentres.map((c, i) => (
                            <div key={i} className="emp-center-card">
                              <div className="emp-center-card-info">
                                <span className="emp-center-icon">🏥</span>
                                <div>
                                  <div className="emp-center-name">{c.centreName}</div>
                                  <div className="emp-center-role">
                                    {c.roles && c.roles.length > 0 ? c.roles.join(', ') : viewEmployee.roleName || 'Staff'}
                                  </div>
                                </div>
                              </div>
                              {c.isDefault && (
                                <span className="emp-center-default-tag">Default Center</span>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="emp-center-card">
                            <div className="emp-center-card-info">
                              <span className="emp-center-icon">🏥</span>
                              <div>
                                <div className="emp-center-name">{viewEmployee.centreName || 'Primary Diagnostic Hub'}</div>
                                <div className="emp-center-role">{viewEmployee.roleName || 'Staff'}</div>
                              </div>
                            </div>
                            <span className="emp-center-default-tag">Default Center</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section 6: Additional Information / Financial & Discount Authority */}
                    <div className="emp-info-card emp-profile-col-full">
                      <div className="emp-info-card-header">
                        <div className="emp-info-card-title-group">
                          <span className="emp-info-card-icon">💳</span>
                          <h3 className="emp-info-card-title">Financial &amp; Billing Authority</h3>
                        </div>
                        <span className="emp-info-card-badge">Discount Permissions</span>
                      </div>
                      <div className="emp-info-grid">
                        <div className="emp-info-field">
                          <span className="emp-info-label">Max Discount / Month</span>
                          <span className="emp-info-value" style={{ color: '#0f766e', fontWeight: 700 }}>
                            ₹{(viewEmployee.discountPerMonth || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Max Discount / Bill</span>
                          <span className="emp-info-value" style={{ color: '#0f766e', fontWeight: 700 }}>
                            {viewEmployee.discountPerBillPer || 0}%
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Discount On Package</span>
                          <span className="emp-info-value">
                            {viewEmployee.discountOnPackage ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Allowed</span>
                            ) : (
                              <span className="emp-info-value empty">Not Allowed</span>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Approve Below Base Rate</span>
                          <span className="emp-info-value">
                            {viewEmployee.appBelowBaseRate ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Authorized</span>
                            ) : (
                              <span className="emp-info-value empty">Not Authorized</span>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Approve Special Rate</span>
                          <span className="emp-info-value">
                            {viewEmployee.approveSpecialRate ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Authorized</span>
                            ) : (
                              <span className="emp-info-value empty">Not Authorized</span>
                            )}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Hide Rate on Bills</span>
                          <span className="emp-info-value">
                            {viewEmployee.isHideRate ? 'Yes (Hidden)' : 'No (Visible)'}
                          </span>
                        </div>
                        <div className="emp-info-field">
                          <span className="emp-info-label">Digital Signature</span>
                          <span className="emp-info-value">
                            {viewEmployee.signaturePath ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Configured</span>
                            ) : (
                              <span className="emp-info-value empty">Not Configured</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ─── Modal Footer ─── */}
                <div className="emp-modal-footer">
                  <div className="emp-modal-footer-meta">
                    Staff ID: <strong>EMP-{String(viewEmployee.employeeId).padStart(4, '0')}</strong> • System Record Active
                  </div>
                  <button
                    type="button"
                    className="emp-btn emp-btn-outline"
                    onClick={() => setIsViewModalOpen(false)}
                    id="btn-footer-close-view"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="emp-btn emp-btn-primary"
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleOpenEdit(viewEmployee.employeeId);
                    }}
                    id="btn-footer-edit-view"
                  >
                    ✎ Edit Staff Member
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* =====================================================================
          STATUS CONFIRMATION MODAL
         ===================================================================== */}
      {statusModal.open && statusModal.employee && (
        <div className="emp-modal-overlay">
          <div className="emp-modal-container emp-confirm-dialog">
            <div className="emp-modal-header">
              <div className="emp-modal-header-title">
                <span style={{ fontSize: '1.4rem' }}>
                  {statusModal.employee.isActive ? '⚠️' : '✅'}
                </span>
                <h2>{statusModal.employee.isActive ? 'Deactivate Staff Member?' : 'Activate Staff Member?'}</h2>
              </div>
              <button
                className="emp-modal-close"
                onClick={() => setStatusModal({ open: false, employee: null })}
              >
                &times;
              </button>
            </div>

            <div className="emp-modal-body">
              <p style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.5 }}>
                {statusModal.employee.isActive ? (
                  <>
                    Are you sure you want to deactivate <strong>{statusModal.employee.name}</strong>?
                    They will immediately lose the ability to log in, validate diagnostics, and sign patient test reports.
                  </>
                ) : (
                  <>
                    Are you sure you want to re-activate <strong>{statusModal.employee.name}</strong>?
                    Their system access, diagnostic role authorities, and report signing capabilities will be immediately restored.
                  </>
                )}
              </p>
            </div>

            <div className="emp-modal-footer">
              <button
                className="emp-btn emp-btn-outline"
                disabled={isStatusToggling}
                onClick={() => setStatusModal({ open: false, employee: null })}
              >
                Cancel
              </button>
              <button
                className={`emp-btn ${statusModal.employee.isActive ? 'emp-btn-danger' : 'emp-btn-success'}`}
                disabled={isStatusToggling}
                onClick={handleConfirmStatusToggle}
              >
                {isStatusToggling ? (
                  <>
                    <span className="lis-spinner-small"></span> Updating...
                  </>
                ) : statusModal.employee.isActive ? (
                  'Yes, Deactivate Staff'
                ) : (
                  'Yes, Activate Staff'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          PASSKEY / BIOMETRIC ENROLLMENT MODAL
         ===================================================================== */}
      {showPasskeyModal && (
        <div className="emp-modal-overlay">
          <div className="emp-modal-container emp-passkey-dialog">
            <div className="emp-modal-header">
              <div className="emp-modal-header-title">
                <span style={{ fontSize: '1.4rem' }}>🖐</span>
                <h2>Register Secure Login / Passkey</h2>
              </div>
              <button
                className="emp-modal-close"
                onClick={() => {
                  if (!isRegisteringPasskey) {
                    setShowPasskeyModal(false);
                    setPasskeyError(null);
                  }
                }}
              >
                &times;
              </button>
            </div>

            <div className="emp-modal-body">
              <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.5, marginBottom: '1.25rem' }}>
                Enrolling biometric passkey for staff member <strong>{formData.name || formData.username}</strong>.
                When you click <em>Start Device Registration</em>, Windows Hello, your fingerprint sensor, or platform authenticator will prompt you to verify.
              </p>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label" style={{ fontWeight: 600 }}>
                  Device Friendly Name <span className="form-req">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={newDeviceFriendlyName}
                  onChange={(e) => setNewDeviceFriendlyName(e.target.value)}
                  placeholder="e.g. Office Laptop, Reception Desk"
                  id="input-device-friendly-name"
                  disabled={isRegisteringPasskey}
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  A recognizable label for this workstation or platform authenticator.
                </span>
              </div>

              {passkeyError && (
                <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                  ⚠️ {passkeyError}
                </div>
              )}

              <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.8rem', color: '#166534', lineHeight: 1.4 }}>
                🔒 <strong>Zero Biometric Storage:</strong> Raw fingerprints or biometric images are never captured or sent to the server. Verification occurs locally on the operating system via FIDO2 / WebAuthn cryptographic proof.
              </div>
            </div>

            <div className="emp-modal-footer">
              <button
                type="button"
                className="emp-btn emp-btn-outline"
                disabled={isRegisteringPasskey}
                onClick={() => {
                  setShowPasskeyModal(false);
                  setPasskeyError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="emp-btn emp-btn-primary"
                disabled={isRegisteringPasskey || !newDeviceFriendlyName.trim()}
                onClick={handleRegisterPasskey}
                id="btn-confirm-register-passkey"
              >
                {isRegisteringPasskey ? (
                  <>
                    <span className="lis-spinner-small"></span> Verifying Authenticator...
                  </>
                ) : (
                  '🖐 Start Device Registration'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* =====================================================================
          ACTIVE WORKSTATION SESSIONS MODAL
         ===================================================================== */}
      {sessionsModal.open && (
        <div className="emp-modal-overlay">
          <div className="emp-modal-container emp-modal-sessions">
            <div className="emp-modal-header" style={{ borderBottom: '1px solid #e2e8f0', padding: '16px 24px', background: '#f8fafc' }}>
              <div className="emp-modal-header-title">
                <span style={{ fontSize: '1.4rem' }}>💻</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>Active Sessions &amp; Connected Devices</h2>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Staff Member: <strong style={{ color: '#1e293b' }}>{sessionsModal.employee?.name}</strong>
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {sessionsModal.sessions.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => {
                      if (window.confirm(`Terminate ALL active sessions for ${sessionsModal.employee?.name}?`)) {
                        handleLogoutAllSessions(sessionsModal.employee?.employeeId);
                      }
                    }}
                    id="btn-logout-all-sessions"
                  >
                    Logout All Sessions
                  </button>
                )}
                <button
                  className="emp-modal-close"
                  onClick={() => setSessionsModal({ open: false, employee: null, sessions: [], loading: false })}
                  aria-label="Close"
                  id="btn-close-sessions-modal"
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="emp-modal-body" style={{ padding: '24px' }}>
              {sessionsModal.loading ? (
                <div className="emp-loading-state" style={{ padding: '40px 0', textAlign: 'center' }}>
                  <div className="lis-spinner" style={{ margin: '0 auto 12px' }}></div>
                  <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading live workstation sessions...</p>
                </div>
              ) : sessionsModal.sessions.length === 0 ? (
                <div className="emp-sessions-empty-state">
                  <div className="emp-sessions-empty-icon">💤</div>
                  <h3>No Active Sessions</h3>
                  <p>This employee is not currently logged into any workstation or browser.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="emp-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>Device &amp; Browser</th>
                        <th style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>IP Address</th>
                        <th style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>Login Time</th>
                        <th style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>Status</th>
                        <th style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#475569', fontWeight: 700, textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionsModal.sessions.map((s) => (
                        <tr key={s.sessionId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                              {s.deviceName || s.device || 'Desktop Workstation'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {s.browser} • {s.operatingSystem}
                            </div>
                            {s.isCurrentSession && (
                              <span style={{ display: 'inline-block', background: '#dbeafe', color: '#1d4ed8', fontSize: '0.68rem', fontWeight: 600, padding: '1px 6px', borderRadius: '4px', marginTop: '2px' }}>
                                Your Current Session
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '0.84rem', fontFamily: 'monospace', color: '#475569' }}>
                            {s.ipAddress || '127.0.0.1'}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: '#475569' }}>
                            {new Date(s.loginTime).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }}></span> Active
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', padding: '4px 10px', borderRadius: '5px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => {
                                if (window.confirm(`Force terminate session on ${s.deviceName || 'device'} (${s.ipAddress})?`)) {
                                  handleForceLogoutSession(s.sessionId);
                                }
                              }}
                            >
                              Force Logout
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="emp-modal-footer">
              <button
                type="button"
                className="emp-btn emp-btn-outline"
                onClick={() => setSessionsModal({ open: false, employee: null, sessions: [], loading: false })}
                id="btn-footer-close-sessions"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Facial Biometric Enrollment Modal */}
      <FaceBiometricModal
        isOpen={showFaceModal}
        onClose={() => setShowFaceModal(false)}
        mode="enroll_employee"
        employeeData={formData}
        onSuccess={handleEnrollFaceSuccess}
      />

      {/* Step-Up Re-Authentication for Sensitive Employee Field Changes */}
      <ReAuthenticateModal
        isOpen={isReAuthModalOpen}
        onClose={() => {
          setIsReAuthModalOpen(false);
          showToast('Save cancelled. Sensitive fields (mobile, email, ID, or password) were not updated.', 'error');
        }}
        onSuccess={(reAuthToken) => {
          setIsReAuthModalOpen(false);
          performSave(reAuthToken);
        }}
        reason="save changes to this employee's sensitive information (mobile, email, ID, or password)"
      />

      {/* =====================================================================
          ADMIN QUICK PASSWORD RESET MODAL
          No admin self-re-auth required — purpose-built for when the employee's
          existing password is unknown/forgotten (calls /auth/admin-reset-password).
         ===================================================================== */}
      {quickResetModal.open && (
        <div className="emp-modal-overlay">
          <div className="emp-modal-container emp-confirm-dialog">
            <div className="emp-modal-header">
              <div className="emp-modal-header-title">
                <span style={{ fontSize: '1.4rem' }}>🔑</span>
                <h2>Reset Login Password</h2>
              </div>
              <button
                className="emp-modal-close"
                onClick={() => setQuickResetModal({ open: false, newPassword: '', forceChange: true, isSaving: false })}
                disabled={quickResetModal.isSaving}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleQuickPasswordReset}>
              <div className="emp-modal-body">
                <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, marginTop: 0 }}>
                  Set a new temporary password for <strong>{formData.firstName ? `${formData.firstName} ${formData.lastName || ''}`.trim() : formData.name}</strong>.
                  All their active sessions will be logged out immediately.
                </p>

                <div className="emp-field-group">
                  <label className="emp-field-label">New Password <span className="emp-field-req">*</span></label>
                  <input
                    type="text"
                    className="emp-field-input"
                    placeholder="Minimum 4 characters"
                    value={quickResetModal.newPassword}
                    onChange={(e) => setQuickResetModal((prev) => ({ ...prev, newPassword: e.target.value }))}
                    autoFocus
                    disabled={quickResetModal.isSaving}
                  />
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#334155', marginTop: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={quickResetModal.forceChange}
                    onChange={(e) => setQuickResetModal((prev) => ({ ...prev, forceChange: e.target.checked }))}
                    disabled={quickResetModal.isSaving}
                  />
                  Force password change on next login
                </label>
              </div>

              <div className="emp-modal-footer">
                <button
                  type="button"
                  className="emp-btn emp-btn-outline"
                  disabled={quickResetModal.isSaving}
                  onClick={() => setQuickResetModal({ open: false, newPassword: '', forceChange: true, isSaving: false })}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="emp-btn emp-btn-success"
                  disabled={quickResetModal.isSaving}
                >
                  {quickResetModal.isSaving ? (
                    <>
                      <span className="lis-spinner-small"></span> Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MasterPageWrapper>
  );
};

export default EmployeePage;
